import { Users, TrendingUp, Award, Heart, Upload } from "lucide-react";
import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Define the schema for form validation
const schema = z.object({
  name: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(1, "Phone number is required"),
  areaOfInterest: z.string().min(1, "Area of interest is required"),
});

type FormData = z.infer<typeof schema>;

interface CVSubmissionFormProps {}

const Careers = () => {
  const [cvFile, setCvFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      areaOfInterest: "",
    },
  });

  const benefits = [
    {
      icon: TrendingUp,
      title: "Professional Development",
      description: "Continuous learning opportunities and career advancement programs"
    },
    {
      icon: Users,
      title: "Collaborative Environment", 
      description: "Work with passionate professionals in a supportive team culture"
    },
    {
      icon: Award,
      title: "Competitive Benefits",
      description: "Attractive salary packages, medical cover, and retirement benefits"
    },
    {
      icon: Heart,
      title: "Community Impact",
      description: "Make a meaningful difference in members' financial well-being"
    }
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF, DOC, or DOCX file.",
          variant: "destructive",
        });
        return;
      }

      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: "Please upload a file smaller than 5MB.",
          variant: "destructive",
        });
        return;
      }

      setCvFile(file);
      toast({
        title: "File uploaded successfully",
        description: `${file.name} has been uploaded.`,
      });
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!cvFile) {
      toast({
        title: "CV Required",
        description: "Please upload your CV/Resume before submitting.",
        variant: "destructive",
      });
      return;
    }

    try {
      const subject = `${data.name} - CV Submission`;
      const body = `Dear Hiring Team,

I am submitting my CV for consideration for future opportunities at Natcon Sacco.

Area of Interest: ${data.areaOfInterest}

Applicant Details:
- Name: ${data.name}
- Email: ${data.email}
- Phone: ${data.phone}

Please find my CV/Resume attached to this email.

Best regards,
${data.name}`;

      // Try to use Web Share API first for seamless attachment
      if (navigator.share && navigator.canShare) {
        try {
          const files = [cvFile];
          if (navigator.canShare({ files })) {
            await navigator.share({
              title: subject,
              text: body,
              files: files,
            });
            
            toast({
              title: "Email Opened Successfully!",
              description: "Your email client has been opened with the CV attached.",
            });
            
            // Reset form after successful submission
            form.reset();
            setCvFile(null);
            return;
          }
        } catch (shareError) {
          console.log("Web Share API failed, using fallback method");
        }
      }

      // Fallback: Open email client with pre-filled content
      const mailtoLink = `mailto:natconsacco@nca.go.ke?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoLink, '_blank');

      toast({
        title: "Email Opened",
        description: "Your email client has been opened. Please attach your CV to complete the submission.",
      });

      // Reset form after opening email
      form.reset();
      setCvFile(null);

    } catch (error) {
      console.error('Error in CV submission:', error);
      toast({
        title: "Error",
        description: "There was an error processing your submission. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="py-16">
        <div className="container max-w-6xl mx-auto px-4">
          {/* Page Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-foreground mb-4">Careers at Natcon Sacco</h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Join our dynamic team and be part of a mission to empower financial growth and prosperity. 
              At Natcon Sacco, we believe our people are our greatest asset, and we're committed to 
              creating an environment where talent thrives.
            </p>
          </div>

          {/* Why Work With Us */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold text-foreground text-center mb-12">Why Work With Us?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit) => (
                <Card key={benefit.title} className="text-center">
                  <CardHeader>
                    <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <benefit.icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CV Submission Form */}
          <section>
            <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <Upload className="h-6 w-6 text-primary" />
                  <CardTitle className="text-2xl">Join Our Talent Pool</CardTitle>
                </div>
                <p className="text-muted-foreground">
                  We're always looking for talented individuals to join our team. Submit your CV and we'll keep you in mind for future opportunities.
                </p>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Full Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter your full name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email Address *</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="Enter your email" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Phone Number *</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter your phone number" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="areaOfInterest"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Area of Interest *</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g., Finance, Marketing, IT" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div>
                          <FormLabel>Upload CV/Resume *</FormLabel>
                          <div 
                            className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground mb-2">
                              {cvFile ? cvFile.name : "Click to upload or drag and drop"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              PDF, DOC, DOCX (Max 5MB)
                            </p>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileSelect}
                              accept=".pdf,.doc,.docx"
                              className="hidden"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6">
                      <Button type="submit" size="lg" className="w-full md:w-auto">
                        Submit Your CV
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </section>

          {/* Contact Info */}
          <div className="mt-16 text-center">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-foreground mb-2">Questions about careers at Natcon Sacco?</h3>
                <p className="text-muted-foreground">
                  Contact our HR Department at <strong>natconsacco@nca.go.ke</strong> or call <strong>+254111485951</strong>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Careers;